import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getProp from "@insite/mobius/utilities/getProp";
import CENQuoteSummaryCard from "@tCentro/Components/CENQuoteSummaryCard";
import CENRecentQuotesCardList from "@tCentro/Components/CENRecentQuotesCardList";
import CENRecentQuotesCardListHeading from "@tCentro/Components/CENRecentQuotesCardListHeading";
import { getDataLayerUtilityV4 } from "@tCentro/Custom/Utilities/getDataLayerUtility";
import { COLORS } from "@tCentro/theme/design";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
    };
}
const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesText: {
        css: css`
            font-family: Poppins;
            font-size: 14px;
            font-weight: 600;
            line-height: 16px;
            text-align: center;
            color: ${getProp("theme.typography.p.color")};
        `,
    },
    noQuotesGridItem: {
        width: 12,
        css: css`
            background-color: #f2f2f2;
            margin: 32px 0px !important;
            padding: 66px 24px 66px 24px;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    cardContainer: {
        gridItem: {
            css: css`
                padding: 0px;
            `,
        },
        cardDivider: {
            css: css`
                margin: 0px;
            `,
        },
    },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes, fields } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl =
            myQuotesPageNavLink && quotesDataView.value.length > 0 ? myQuotesPageNavLink.url : undefined;

        return (
            <StyledWrapper>
                <StyledWrapper
                    css={css`
                        height: 16px;
                        background: ${this.props.fields.backgroundColor};
                    `}
                ></StyledWrapper>
                <StyledWrapper
                    css={css`
                        border: 4px solid #f2f2f2;
                        height: 100%;
                    `}
                >
                    <CENRecentQuotesCardListHeading
                        heading={translate("Quotes to Review")}
                        viewAllUrl={myQuotesPageUrl}
                    />
                    <CENRecentQuotesCardList>
                        {quotesDataView.value.length === 0 && (
                            <GridItem {...styles.noQuotesGridItem}>
                                <Typography {...styles.noQuotesText}>{siteMessage("Rfq_NoQuotesMessage")}</Typography>
                            </GridItem>
                        )}
                        {quotesDataView.value.map(quote => {
                            const onClickHandler = (index: number) => {
                                if (!Number.isInteger(index) || index < 0 || index > 2) {
                                    return;
                                }

                                const getIndexedProperty = (property?: string) => property?.split("|")[index] || "";
                                const price = Number(getIndexedProperty(quote.properties?.top3ProductsPrices) || "0");
                                const value = Number(getIndexedProperty(quote.properties?.top3ProductsSubTotals) || "0");
                                const dataLayerObject = {
                                    item_id: getIndexedProperty(quote.properties?.top3ProductsMPNs),
                                    item_name: getIndexedProperty(quote.properties?.top3ProductsTitles),
                                    item_brand: getIndexedProperty(quote.properties?.top3ProductsBrands),
                                    price,
                                    quantity: Number(
                                        getIndexedProperty(quote.properties?.top3ProductsQtyOrdered) || "1",
                                    ),
                                    value,
                                    item_list_id: "my_account_recent_quotes",
                                    item_list_name: "My Account: Recent Quotes",
                                };

                                if (dataLayerObject.item_id) {
                                    getDataLayerUtilityV4(dataLayerObject);
                                }
                            }
                            return (
                                <CardContainer key={quote.id} extendedStyles={styles.cardContainer}>
                                    <CENQuoteSummaryCard
                                        quote={quote}
                                        session={session}
                                        quoteSettings={settingsCollection.quoteSettings}
                                        extendedStyles={styles.quoteSummaryCard}
                                        onClickHandler={onClickHandler}
                                    />
                                </CardContainer>
                            )
                        })}
                    </CENRecentQuotesCardList>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                editorTemplate: "TextField",
                defaultValue: "linear-gradient(90deg,#266b40 27.08%,#2d804c 71.87%,#43687a 100%)",
                fieldType: "General",
                displayName: "Background Color",
            },
        ],
    },
};

export default widgetModule;
